import { FinancialSummaryMath } from '../../FinancialSummaryMath/FinancialSummaryMath';
import { buildSiblingGuaranteeExpenses } from './siblingGuaranteeExpenses';
import { isPoolPreviewActive, buildPoolEngineOffer } from './buildPoolEngineInput';

const runMath = (offer, budget) => {
  try {
    const calculator = new FinancialSummaryMath(offer, budget, false);
    return JSON.parse(calculator.calculate());
  } catch (e) {
    console.error('Event venue-net calculation failed:', e);
    return null;
  }
};

const netAfterFinalExpenses = (showSummary, mode) => {
  if (!showSummary) return 0;
  const finalKey = {
    offer: 'showNetAfterFinalExpenses',
    forecast: 'showNetAfterFinalExpensesForecast',
    actual: 'showNetAfterFinalExpensesActual',
  }[mode];
  // FinancialSummaryMath always emits a number for the final-expenses keys.
  return showSummary[finalKey] || 0;
};

const ancillaryNet = (showSummary, mode) => {
  if (!showSummary) return 0;
  const ancillaryKey = {
    offer: 'ancillaryNet',
    forecast: 'ancillaryNetForecast',
    actual: 'ancillaryNetActual',
  }[mode];
  return showSummary[ancillaryKey] || 0;
};

const artistDue = (calc, mode) => {
  if (!calc) return 0;
  const earnings = {
    offer: calc.artistEarnings,
    forecast: calc.artistEarningsForecast,
    actual: calc.artistEarningsActual,
  }[mode];
  return earnings?.artistTotalDue || 0;
};

export function buildEventVenueNet(eventOffers, eventBudget, activeOffer) {
  const baseOffers = (eventOffers || []).filter(Boolean);
  // The active tab's entry in eventOffers is the SSR snapshot — it only
  // refreshes on tab switch, so live deal edits live on `activeOffer`. Splice
  // the live copy over its stale entry (matched by id) so Venue Net moves as
  // the current artist's guarantee/% is edited, the way the per-tab number did
  // before this rollup. When the active offer isn't in the list, leave it as-is.
  const offers = activeOffer?.id
    ? baseOffers.map((o) => (o.id === activeOffer.id ? activeOffer : o))
    : baseOffers;
  if (offers.length === 0) return {};
  if (!eventBudget || Object.keys(eventBudget).length === 0) return {};

  // Shared base: real expenses only. Net-after-final-expenses is independent
  // of any single offer's deal, so the first offer can drive it.
  const base = runMath(offers[0], eventBudget);
  if (!base) return {};

  const modes = ['offer', 'forecast', 'actual'];
  const totalDue = { offer: 0, forecast: 0, actual: 0 };

  if (isPoolPreviewActive(activeOffer, activeOffer?.lineup)) {
    // O-2783: in Pool mode the lineup shares ONE pool, so the event's total
    // artist cost is the pool's door-deal payout — NOT the sum of the individual
    // offers, whose own guarantees are 0 in pool mode. Compute it once from the
    // pool engine offer (the same input the editor preview uses) so the pool
    // guarantee flows through to Venue Net.
    const poolCalc = runMath(buildPoolEngineOffer(activeOffer, activeOffer.lineup), eventBudget);
    modes.forEach((mode) => { totalDue[mode] = artistDue(poolCalc, mode); });
  } else {
    offers.forEach((offer) => {
      const siblingExpenses = buildSiblingGuaranteeExpenses(offer, offers);
      const budgetForOffer = {
        ...eventBudget,
        expense_line_items: [
          ...siblingExpenses,
          ...(eventBudget.expense_line_items || []),
        ],
      };
      const calc = runMath(offer, budgetForOffer);
      modes.forEach((mode) => {
        totalDue[mode] += artistDue(calc, mode);
      });
    });
  }

  return modes.reduce((acc, mode) => {
    acc[mode] = netAfterFinalExpenses(base.showSummary, mode)
      - totalDue[mode]
      + ancillaryNet(base.showSummary, mode);
    return acc;
  }, {});
}
